import React from 'react';
import { X, ShieldCheck, Lock, Key, Activity, AlertTriangle, BookOpen, Award } from 'lucide-react';

const SecurityModal = ({ securityModalOpen, setSecurityModalOpen }) => {
  const securityPractices = [
    {
      icon: <ShieldCheck className="w-5 h-5 text-blue-600 dark:text-blue-400" />,
      title: "Enterprise-Grade Encryption",
      content: "All data is protected using AES-256 encryption for data at rest and TLS 1.3 for data in transit. We implement cryptographic best practices including perfect forward secrecy and regular key rotation."
    },
    {
      icon: <Lock className="w-5 h-5 text-blue-600 dark:text-blue-400" />,
      title: "Zero Trust Architecture",
      content: "Our systems enforce least-privilege access with mandatory multi-factor authentication (MFA) for all administrative access. Just-in-time elevation and continuous authentication ensure strict access controls."
    },
    {
      icon: <Key className="w-5 h-5 text-blue-600 dark:text-blue-400" />,
      title: "Identity & Access Management",
      content: "Role-based access control (RBAC) with attribute-based conditions. All access attempts are logged and monitored through our SIEM system with anomaly detection capabilities."
    },
    {
      icon: <Activity className="w-5 h-5 text-blue-600 dark:text-blue-400" />,
      title: "Continuous Monitoring",
      content: "24/7 security operations center monitoring with AI-driven threat detection. We maintain 365-day log retention with immutable audit trails for forensic investigations."
    },
    {
      icon: <AlertTriangle className="w-5 h-5 text-blue-600 dark:text-blue-400" />,
      title: "Incident Response",
      content: "Certified incident response team with 1-hour SLA for critical events. Our playbooks cover ransomware, data breaches, and DDoS attacks with regular red team exercises."
    },
    {
      icon: <BookOpen className="w-5 h-5 text-blue-600 dark:text-blue-400" />,
      title: "Compliance Framework",
      content: "We maintain SOC 2 Type II, ISO 27001, and GDPR compliance. Regular third-party audits validate our security controls with publicly available attestation reports."
    },
    {
      icon: <Award className="w-5 h-5 text-blue-600 dark:text-blue-400" />,
      title: "Secure Development",
      content: "All code undergoes static/dynamic analysis, manual review, and pentesting before deployment. We follow OWASP ASVS and maintain a bug bounty program."
    }
  ];

  return (
    <>
      {securityModalOpen && (
        <div 
          className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setSecurityModalOpen(false)}
        >
          <div 
            className="bg-white dark:bg-gray-900 rounded-xl max-w-full w-full sm:max-w-3xl lg:max-w-4xl p-6 shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-2xl font-bold dark:text-white flex items-center gap-2">
                  <ShieldCheck className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  Security & Compliance Framework
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Last updated: {new Date().toLocaleDateString()}
                </p>
              </div>
              <button 
                onClick={() => setSecurityModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label="Close security information"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="prose dark:prose-invert max-w-none">
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Our Security Commitment</h4>
                <p className="text-gray-700 dark:text-gray-300">
                  At 0x10LayerFF, security is foundational to our platform. We implement defense-in-depth strategies across our 
                  infrastructure, applications, and operations to protect customer data. Our security program aligns with 
                  NIST Cybersecurity Framework and undergoes independent validation.
                </p>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                {securityPractices.map((practice, index) => (
                  <div 
                    key={index} 
                    className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5">
                        {practice.icon}
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-900 dark:text-white mb-1.5">
                          {practice.title}
                        </h5>
                        <p className="text-gray-700 dark:text-gray-300 text-sm">
                          {practice.content}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Certifications & Attestations</h4>
                <div className="flex flex-wrap gap-4">
                  <div className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-full text-xs font-medium">
                    SOC 2 Type II
                  </div>
                  <div className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-full text-xs font-medium">
                    ISO 27001:2022
                  </div>
                  <div className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-full text-xs font-medium">
                    GDPR Compliant
                  </div>
                  <div className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-full text-xs font-medium">
                    NIST CSF Aligned
                  </div>
                </div>
              </div>

              <div className="mt-8 flex justify-between items-center">
                <a 
                  href="/security-whitepaper.pdf" 
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium flex items-center gap-1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download Full Security Whitepaper
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                </a>
                <button 
                  onClick={() => setSecurityModalOpen(false)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  I Understand
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SecurityModal;